import React, { useState, useEffect } from 'react';
import { Navigate, Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { AlertCircle, Loader2, Mail, CheckCircle } from 'lucide-react';

export default function VerifyEmailPage() {
  const { isAuthenticated, loading: authLoading, confirmSignup, resendCode, login, error, clearError } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [email, setEmail] = useState(location.state?.email || '');
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [formError, setFormError] = useState('');
  const [success, setSuccess] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);

  useEffect(() => {
    // Clear resend success message after 5 seconds
    if (resendSuccess) {
      const timer = setTimeout(() => setResendSuccess(false), 5000);
      return () => clearTimeout(timer);
    }
  }, [resendSuccess]);

  if (authLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError('');
    clearError();

    if (!email || !code) {
      setFormError('Please enter your email and verification code');
      return;
    }

    setLoading(true);
    try {
      await confirmSignup(email, code);
      setSuccess(true);
      // Redirect to login after a short delay
      setTimeout(() => {
        navigate('/login', { state: { message: 'Email verified! Please sign in.' } });
      }, 2000);
    } catch (err) {
      setFormError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    if (!email) {
      setFormError('Please enter your email address');
      return;
    }

    setResending(true);
    setFormError('');
    try {
      await resendCode(email);
      setResendSuccess(true);
    } catch (err) {
      setFormError(err.message);
    } finally {
      setResending(false);
    }
  };

  const displayError = formError || error;

  if (success) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-background p-4">
        <div className="w-full max-w-md space-y-6">
          <Card>
            <CardContent className="pt-6">
              <div className="flex flex-col items-center space-y-4 text-center">
                <div className="h-12 w-12 rounded-full bg-green-500/15 flex items-center justify-center">
                  <CheckCircle className="h-6 w-6 text-green-500" />
                </div>
                <CardTitle>Email Verified!</CardTitle>
                <CardDescription>
                  Your email has been verified successfully. Redirecting to login...
                </CardDescription>
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-background p-4">
      <div className="w-full max-w-md space-y-6">
        {/* Logo */}
        <div className="flex flex-col items-center space-y-2">
          <a href="https://trucontext.ai/" className="flex items-center space-x-2 hover:opacity-80 transition-opacity">
            <div className="h-10 w-10 rounded-lg bg-primary flex items-center justify-center">
              <span className="text-lg font-bold text-primary-foreground">TC</span>
            </div>
            <span className="text-2xl font-bold">TruContext</span>
          </a>
          <p className="text-sm text-muted-foreground">Developer Portal</p>
        </div>

        <Card>
          <CardHeader className="space-y-1">
            <div className="flex justify-center mb-2">
              <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center">
                <Mail className="h-6 w-6 text-primary" />
              </div>
            </div>
            <CardTitle className="text-2xl text-center">Check your email</CardTitle>
            <CardDescription className="text-center">
              We sent a verification code to{' '}
              {email ? <span className="font-medium text-foreground">{email}</span> : 'your email'}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {displayError && (
              <div className="flex items-center gap-2 p-3 text-sm bg-red-500/20 border border-red-500/50 text-red-400 rounded-md">
                <AlertCircle className="h-4 w-4 flex-shrink-0" />
                <span>{displayError}</span>
              </div>
            )}

            {resendSuccess && (
              <div className="flex items-center gap-2 p-3 text-sm text-green-500 bg-green-500/15 rounded-md">
                <CheckCircle className="h-4 w-4" />
                <span>Verification code sent! Check your email.</span>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              {!location.state?.email && (
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="you@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    disabled={loading}
                    autoComplete="email"
                  />
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="code">Verification Code</Label>
                <Input
                  id="code"
                  type="text"
                  placeholder="Enter 6-digit code"
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  disabled={loading}
                  autoComplete="one-time-code"
                  className="text-center text-lg tracking-widest"
                  maxLength={6}
                />
              </div>

              <Button type="submit" className="w-full" disabled={loading || code.length !== 6}>
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Verifying...
                  </>
                ) : (
                  'Verify Email'
                )}
              </Button>
            </form>

            <div className="text-center space-y-2">
              <p className="text-sm text-muted-foreground">
                Didn't receive the code?
              </p>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleResendCode}
                disabled={resending}
                className="text-primary"
              >
                {resending ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Sending...
                  </>
                ) : (
                  'Resend code'
                )}
              </Button>
            </div>

            <p className="text-center text-sm text-muted-foreground">
              <Link
                to="/login"
                className="underline underline-offset-4 hover:text-primary"
              >
                Back to sign in
              </Link>
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
